import React, { useEffect, useState } from 'react';
import OrderStatusProgress from 'components/order-status-progress';
import { ENUM_ORDER_PROGRESS } from 'utils/enum';
import GoogleMapReact from 'components/google-map-react';
import { OrderType } from 'models/order.model';
import kitchenMarker from 'assets/images/kitchen-map-marker.png';
import userMarker from 'assets/images/user-map-marker.png';
import { apiService } from 'services/base.service';
import { ReactComponent as DirectionIcon } from 'assets/icons/map-direction.svg';

type MarkerProps = {
  iconUrl: string;
  lat: number;
  lng: number;
};

const Marker = (props: MarkerProps) => {
  const { iconUrl } = props;
  return <img className='w-8 h-10 -ml-4 -mt-10' src={iconUrl} />;
};

type Props = {
  step: ENUM_ORDER_PROGRESS;
  order: OrderType;
  onViewTimeline: (step?: ENUM_ORDER_PROGRESS) => void;
};

type LocationsType = {
  origin: google.maps.LatLngLiteral;
  destination: google.maps.LatLngLiteral;
};

const OrderInTransit = (props: Props) => {
  const { order, step, onViewTimeline } = props;
  const [map, setMap] = useState<google.maps.Map>();
  const [locations, setLocations] = useState<LocationsType>();

  const getCokitchenLocation = async () => {
    const res: any = await apiService.post(`internal/location`, {
      baseURL: process.env.REACT_APP_ORDER_API_URL,
      isInternal: true,
      data: {
        lat: Number(order.calculated_order.lat),
        lng: Number(order.calculated_order.lng)
      }
    });
    return { lat: Number(res.data.cokitchen.lat), lng: Number(res.data.cokitchen.lng) };
  };

  const getLocations = async () => {
    try {
      const origin = await getCokitchenLocation();
      const destination = {
        lat: Number(order.calculated_order.lat),
        lng: Number(order.calculated_order.lng)
      };
      setLocations({ origin, destination });
    } catch (error) {
      console.log(error);
    }
  };

  const getMapLink = () => {
    const url = 'https://www.google.com/maps/dir/?api=1&';
    if (!locations) return url;
    const parameters = new URLSearchParams({
      origin: `${locations.origin.lat},${locations.origin.lng}`,
      destination: `${locations.destination.lat},${locations.destination.lng}`
    }).toString();
    return url + parameters;
  };

  useEffect(() => {
    getLocations();
  }, [order]);

  useEffect(() => {
    if (!map || !locations) return;
    const polyline = new google.maps.Polyline({
      path: [locations.origin, locations.destination],
      geodesic: true,
      strokeColor: '#E60A2B',
      strokeOpacity: 1.0,
      strokeWeight: 4
    });
    polyline.setMap(map);
  }, [map, locations]);

  return (
    <div className='relative overflow-hidden rounded-[10px] bg-[#F8F7F7]'>
      <div className='absolute z-50 top-[6px] left-[6px] w-[calc(100%-12px)] shadow-lg rounded-[10px]'>
        <OrderStatusProgress order={order} step={step} onViewTimeline={onViewTimeline} />
      </div>
      <div className='w-full h-[70vh]'>
        {locations ? (
          <GoogleMapReact
            defaultZoom={15}
            defaultCenter={{ lat: locations.origin.lat, lng: locations.origin.lng }}
            layerTypes={['TransitLayer']}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={({ map }) => setMap(map)}
            options={{
              zoomControl: false,
              fullscreenControl: false,
              mapTypeControl: false,
              streetViewControl: false
            }}>
            <Marker lat={locations.origin.lat} lng={locations.origin.lng} iconUrl={kitchenMarker} />
            <Marker
              lat={locations.destination.lat}
              lng={locations.destination.lng}
              iconUrl={userMarker}
            />
          </GoogleMapReact>
        ) : (
          <div className='w-full h-[70vh] bg-[#F2F2F2]' />
        )}
      </div>
      <a
        target='_blank'
        rel='noreferrer'
        href={getMapLink()}
        className='absolute bottom-4 right-4 flex items-center gap-1 px-3 py-[5px] bg-white rounded-[100px] text-sm text-[#E60A2B] shadow-lg'>
        <DirectionIcon />
        Show direction
      </a>
    </div>
  );
};

export default OrderInTransit;
